import { isGroupTableQuery } from 'owa-group-utils';
import { KeydownConfig } from 'owa-hotkeys';
import { getCommands } from 'owa-mail-hotkeys/lib/utils/MailModuleHotKeys';
import { lazyOnKeyboardMarkAsRead } from 'owa-mail-mark-read-actions';
import { lazyOnKeyboardForward, lazyOnKeyboardReply } from 'owa-mail-message-actions';
import { lazyExpandCollapseAllItemParts } from 'owa-mail-reading-pane-store';
import isAllItemPartsExpanded from 'owa-mail-reading-pane-store/lib/utils/isAllItemPartsExpanded';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import {
    lazyOnKeyboardArchive,
    lazyOnKeyboardToggleFlagState,
    lazyOnKeyboardMarkAsJunkNotJunk,
    lazyOnKeyboardDelete,
} from 'owa-mail-triage-action';
import {
    getIsEverythingSelectedInTable,
    listViewStore,
    TableView,
    SelectionDirection,
    isConversationView,
} from 'owa-mail-list-store';
import {
    lazyOnOpenEmail,
    lazyOnKeyboardUpDown,
    lazyOnKeyboardToggleSelect,
} from 'owa-mail-actions';
import {
    keyboardMultiSelectRow,
    toggleSelectAllRows,
    singleSelectRow,
    resetSelection,
    keyboardCollapseConversation,
} from 'owa-mail-actions/lib/mailListSelectionActions';
import { navigateToTopOfListView } from '../../utils/navigateToTopOfListView';
import MailListContextMenuType from 'owa-mail-list-store/lib/store/schema/MailListContextMenuType';
import { isFeatureEnabled } from 'owa-feature-flags';
import { showMailItemContextMenu } from 'owa-mail-list-actions/lib/actions/itemContextMenuActions';

export function setupMailListContentKeys(tableViewId: string): KeydownConfig[] {
    return [
        { command: getCommands().openMail, handler: () => openMail(tableViewId) },
        {
            command: getCommands().openMailInPopout,
            handler: () => openMailInPopout(tableViewId),
        },
        {
            command: getCommands().home,
            handler: () =>
                navigateToTopOfListView(tableViewId, MailListItemSelectionSource.KeyboardHome),
        },
        { command: getCommands().end, handler: () => end(tableViewId) },
        { command: getCommands().selectAll, handler: () => selectAll(tableViewId) },
        {
            command: getCommands().deselectAll,
            handler: () => deselectAll(tableViewId),
        },
        {
            command: 'down',
            handler: () => keyboardUpDownSelectMailItem(tableViewId, SelectionDirection.Next),
            options: { stopPropagation: false },
        },
        {
            command: 'up',
            handler: () => keyboardUpDownSelectMailItem(tableViewId, SelectionDirection.Previous),
            options: { stopPropagation: false },
        },
        {
            command: 'ctrl+down',
            handler: () => focusNextRow(tableViewId),
            options: { stopPropagation: false },
        },
        {
            command: 'ctrl+up',
            handler: () => focusPreviousRow(tableViewId),
            options: { stopPropagation: false },
        },
        {
            command: 'shift+down',
            handler: () => keyboardShiftMultiSelectNextRow(tableViewId),
            options: { stopPropagation: false },
        },
        {
            command: 'shift+ctrl+down',
            handler: () => keyboardCtrlShiftMultiSelectNextRow(tableViewId),
            options: { stopPropagation: false },
        },
        {
            command: 'shift+up',
            handler: () => multiselectPrevRow(tableViewId),
            options: { stopPropagation: false },
        },
        {
            command: 'shift+ctrl+up',
            handler: () => keyboardCtrlShiftMultiSelectPreviousRow(tableViewId),
            options: { stopPropagation: false },
        },
        {
            command: getCommands().selectUnselectMessage,
            handler: () => keyboardToggleSelect(tableViewId),
        },
        {
            command: getCommands().expandCollapseAll,
            handler: () => expandCollapseConversationItems(tableViewId),
        },
        {
            command: getCommands().expandAll,
            handler: () => expandCollapseConversationItems(tableViewId, true /*expand*/),
        },
        {
            command: getCommands().collapseAll,
            handler: () => expandCollapseConversationItems(tableViewId, false /*expand*/),
        },
    ];
}

export function setupTriageActionKeys(tableViewId: string): KeydownConfig[] {
    const keys = [
        {
            command: getCommands().deleteMail,
            handler: () => deleteRow(tableViewId),
        },
        {
            command: getCommands().softDeleteMail,
            handler: () => softDeleteRow(tableViewId),
        },
        { command: getCommands().forward, handler: () => forwardRow(tableViewId) },
        {
            command: getCommands().replyAll,
            handler: () => replyAllToRow(tableViewId),
        },
        { command: getCommands().reply, handler: () => replyToRow(tableViewId) },
        {
            command: getCommands().toggleFlag,
            handler: () => toggleFlag(tableViewId),
        },
        {
            command: getCommands().markAsRead,
            handler: () => markAsReadRow(tableViewId),
        },
        {
            command: getCommands().markAsUnread,
            handler: () => markAsUnreadRow(tableViewId),
        },
        {
            command: getCommands().markAsJunk,
            handler: () => markAsJunk(tableViewId),
        },
        {
            command: getCommands().archiveMail,
            handler: () => archiveRow(tableViewId),
        },
    ];

    if (isFeatureEnabled('tri-globalHotkeys')) {
        keys.push(
            {
                command: getCommands().moveToFolder,
                handler: () => moveToShortcutHandler(tableViewId, MailListContextMenuType.MoveTo),
            },
            {
                command: getCommands().categorize,
                handler: () =>
                    moveToShortcutHandler(tableViewId, MailListContextMenuType.Categorize),
            }
        );
    }

    return keys;
}

function moveToShortcutHandler(tableViewId: string, ContextMenuType: MailListContextMenuType) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const selectedRowKeys = [...tableView.selectedRowKeys.keys()];

    // moving messages shorcut is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    if (selectedRowKeys.length > 0) {
        const element = document.getElementById(selectedRowKeys[0]);
        const clientRect = element.getBoundingClientRect();

        /**
         * If element isn't visible (i.e. SLV with RP open), then the clientRect
         * object won't have useful anchor information.
         */
        if (clientRect.height > 0) {
            showMailItemContextMenu({ x: clientRect.right, y: clientRect.top }, ContextMenuType);
        }
    }
}

function expandCollapseConversationItems(tableViewId: string, expand?: boolean) {
    const tableView = getTableView(tableViewId);
    const selectedRowKeys = [...tableView?.selectedRowKeys.keys()];

    /**
     * Expand collapse only when in conversation view and
     * a single rowKey is selected.
     */
    if (isConversationView(tableView) && selectedRowKeys.length == 1) {
        const rowKey = selectedRowKeys[0];

        const shouldExpand = expand != null ? expand : !isAllItemPartsExpanded(rowKey);
        lazyExpandCollapseAllItemParts.importAndExecute(
            rowKey,
            shouldExpand /*shouldExpand*/,
            true /*isFromShortcut*/
        );

        if (shouldExpand) {
            singleSelectRow(
                tableView,
                rowKey,
                true /* isUserNavigation */,
                MailListItemSelectionSource.MailListItemTwisty
            );
        } else {
            keyboardCollapseConversation(rowKey);
        }
    }
}

function onKeyboardMultiSelectRow(
    tableViewId: string,
    selectionDirection: SelectionDirection,
    isCtrlOrCmdKeyDown: boolean
) {
    keyboardMultiSelectRow(
        getTableView(tableViewId),
        MailListItemSelectionSource.KeyboardUpDown,
        selectionDirection,
        isCtrlOrCmdKeyDown
    );
}

function keyboardShiftMultiSelectNextRow(tableViewId: string) {
    onKeyboardMultiSelectRow(tableViewId, SelectionDirection.Next, false /* isCtrlOrCmdKeyDown */);
}

function keyboardCtrlShiftMultiSelectNextRow(tableViewId: string) {
    onKeyboardMultiSelectRow(tableViewId, SelectionDirection.Next, true /* isCtrlOrCmdKeyDown */);
}

function keyboardToggleSelect(tableViewId: string) {
    lazyOnKeyboardToggleSelect.importAndExecute(
        getTableView(tableViewId),
        MailListItemSelectionSource.KeyboardCtrlSpace
    );
}

function multiselectPrevRow(tableViewId: string) {
    onKeyboardMultiSelectRow(
        tableViewId,
        SelectionDirection.Previous,
        false /* isCtrlOrCmdKeyDown */
    );
}

function keyboardCtrlShiftMultiSelectPreviousRow(tableViewId: string) {
    onKeyboardMultiSelectRow(
        tableViewId,
        SelectionDirection.Previous,
        true /* isCtrlOrCmdKeyDown */
    );
}

async function keyboardUpDownSelectMailItem(
    tableViewId: string,
    selectionDirection: SelectionDirection
) {
    lazyOnKeyboardUpDown.importAndExecute(
        selectionDirection,
        getTableView(tableViewId),
        MailListItemSelectionSource.KeyboardUpDown,
        true /* shouldSelect */
    );
}

function focusNextRow(tableViewId: string) {
    lazyOnKeyboardUpDown.importAndExecute(
        SelectionDirection.Next,
        getTableView(tableViewId),
        MailListItemSelectionSource.KeyboardUpDown,
        false /* shouldSelect */
    );
}

function focusPreviousRow(tableViewId: string) {
    lazyOnKeyboardUpDown.importAndExecute(
        SelectionDirection.Previous,
        getTableView(tableViewId),
        MailListItemSelectionSource.KeyboardUpDown,
        false /* shouldSelect */
    );
}

function selectAll(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    if (!getIsEverythingSelectedInTable(tableView)) {
        toggleSelectAllRows(tableView);
    }
}

// deselectAll is tied to esc key in certain key sets
// We stop propagation only when we toggleCheckAll, else
// we let the event to propagate
function deselectAll(tableViewId: string) {
    const tableView = getTableView(tableViewId);

    if (getIsEverythingSelectedInTable(tableView)) {
        toggleSelectAllRows(tableView);
    } else {
        resetSelection(tableView, MailListItemSelectionSource.Reset);
    }
}

function end(tableViewId: string) {
    const tableView = getTableView(tableViewId);

    if (tableView.rowKeys.length > 0) {
        singleSelectRow(
            tableView,
            tableView.rowKeys[tableView.rowKeys.length - 1], // Select last row in view
            true /* isUserNavigation */,
            MailListItemSelectionSource.KeyboardEnd
        );
    }
}

function openMail(tableViewId: string) {
    const tableView = getTableView(tableViewId);

    lazyOnOpenEmail.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()],
        MailListItemSelectionSource.KeyboardEnter
    );
}

function openMailInPopout(tableViewId: string) {
    const tableView = getTableView(tableViewId);

    lazyOnOpenEmail.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()],
        MailListItemSelectionSource.KeyboardShiftEnter
    );
}

function archiveRow(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    // archiving is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    lazyOnKeyboardArchive.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()]
    );
}

function markAsJunk(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    // mark as junk is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    lazyOnKeyboardMarkAsJunkNotJunk.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()]
    );
}

function markAsReadRow(tableViewId: string) {
    markAsReadUnreadInternal(tableViewId, true /* isReadValueToSet */);
}

function markAsUnreadRow(tableViewId: string) {
    markAsReadUnreadInternal(tableViewId, false /* isReadValueToSet */);
}

function markAsReadUnreadInternal(tableViewId: string, isReadValueToSet: boolean) {
    const tableView = getTableView(tableViewId);
    const isInVirtualSelectAllMode = tableView.isInVirtualSelectAllMode;
    const rowKeysToActOn = isInVirtualSelectAllMode ? [] : [...tableView.selectedRowKeys.keys()];
    const exclusionList = isInVirtualSelectAllMode ? tableView.virtualSelectAllExclusionList : [];
    lazyOnKeyboardMarkAsRead.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        isInVirtualSelectAllMode,
        rowKeysToActOn,
        exclusionList,
        isReadValueToSet
    );
}

function toggleFlag(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    // toggle flag is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    lazyOnKeyboardToggleFlagState.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()]
    );
}

function replyAllToRow(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    lazyOnKeyboardReply.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()],
        true /* isReplyAll */
    );
}

function replyToRow(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    // Reply (little-r) is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    lazyOnKeyboardReply.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()],
        false /* isReplyAll */
    );
}

function forwardRow(tableViewId: string) {
    const tableView = getTableView(tableViewId);

    lazyOnKeyboardForward.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()]
    );
}

function deleteRow(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    // deleting is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    lazyOnKeyboardDelete.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()],
        tableView.isInVirtualSelectAllMode,
        false /* isSoftDelete */
    );
}

function softDeleteRow(tableViewId: string) {
    const tableView = getTableView(tableViewId);
    // deleting is disabled for groups
    if (isGroupTableQuery(tableView.tableQuery)) {
        return;
    }

    lazyOnKeyboardDelete.importAndExecute(
        tableView,
        listViewStore.expandedConversationViewState.selectedNodeIds,
        [...tableView.selectedRowKeys.keys()],
        tableView.isInVirtualSelectAllMode,
        true /* isSoftDelete */
    );
}

function getTableView(tableViewId: string): TableView {
    return listViewStore.tableViews.get(tableViewId);
}
